export const STEP_TYPES = [
  'message',
  'wait',
  'condition',
  'ai',
  'http',
  'stop',
];

export const CONDITION_ATTRIBUTES = ['pipeline_stage'];

export const CONDITION_OPERATORS = ['equals', 'not_equals'];

export const TRIGGER_TYPES = ['no_reply', 'stage_entry'];

let localIdCounter = 0;

export const nextLocalId = () => {
  localIdCounter += 1;
  return `new-${Date.now()}-${localIdCounter}`;
};

export const defaultStepPayload = stepType => {
  switch (stepType) {
    case 'message':
      return {
        body: '',
        template_name: '',
        template_language: '',
        variables: [],
      };
    case 'wait':
      return { minutes: 60 };
    case 'condition':
      return {
        attribute: 'pipeline_stage',
        operator: 'equals',
        value: '',
        then_position: null,
      };
    case 'http':
      return { url: '' };
    default:
      return {};
  }
};

export const newStep = stepType => ({
  localId: nextLocalId(),
  id: null,
  step_type: stepType,
  position: 0,
  payload: defaultStepPayload(stepType),
});

export const normalizeSteps = (apiSteps = []) =>
  apiSteps.map((step, index) => ({
    localId: `saved-${step.id}`,
    id: step.id,
    step_type: step.step_type,
    position: index,
    payload: { ...(step.payload || {}) },
  }));

export const withPositions = steps =>
  steps.map((step, index) => ({ ...step, position: index }));

const isBlank = value => String(value ?? '').trim() === '';

export const validateSteps = (steps = []) => {
  const errors = [];

  steps.forEach((step, index) => {
    const payload = step.payload || {};

    if (!STEP_TYPES.includes(step.step_type)) {
      errors.push({ index, field: 'step_type', code: 'invalid' });
      return;
    }

    if (step.step_type === 'message') {
      if (isBlank(payload.body) && isBlank(payload.template_name)) {
        errors.push({ index, field: 'payload', code: 'blank' });
      }
    }

    if (step.step_type === 'wait') {
      if (!Number(payload.minutes) > 0) {
        errors.push({ index, field: 'payload', code: 'blank' });
      }
    }

    if (step.step_type === 'condition') {
      if (
        isBlank(payload.attribute) ||
        payload.then_position === null ||
        payload.then_position === undefined
      ) {
        errors.push({ index, field: 'payload', code: 'blank' });
      } else {
        const target = Number(payload.then_position);
        if (!Number.isInteger(target) || target < 0 || target >= steps.length) {
          errors.push({ index, field: 'payload', code: 'invalid' });
        }
      }
    }

    if (step.step_type === 'http' && isBlank(payload.url)) {
      errors.push({ index, field: 'payload', code: 'blank' });
    }
  });

  return errors;
};

export const buildStepsAttributes = (steps = [], removedStepIds = []) => {
  const stepAttributes = withPositions(steps).map(step => {
    const entry = {
      step_type: step.step_type,
      position: step.position,
      payload: step.payload || {},
    };
    if (step.id) entry.id = step.id;
    return entry;
  });

  removedStepIds.forEach(id => {
    stepAttributes.push({ id, _destroy: true });
  });

  return stepAttributes;
};

// Raw display data for simulation entries; the component formats labels via i18n
// so no English strings leak into other locales.
export const stepDetail = step => {
  const payload = step.payload || {};
  switch (step.step_type) {
    case 'message':
      return {
        text:
          payload.template_name || (payload.body || '').slice(0, 48) || null,
      };
    case 'wait':
      return { minutes: payload.minutes || 0 };
    case 'condition':
      return {
        attribute: payload.attribute || null,
        operator: payload.operator || null,
        value: payload.value ?? null,
      };
    case 'http':
      return { url: payload.url || null };
    default:
      return {};
  }
};

// Re-indexes condition jump targets after a reorder/delete: then_position stores
// a positional index, so translate old index -> localId -> new index.
export const remapJumpTargets = (beforeIds, steps) => {
  const afterIds = steps.map(step => step.localId);
  steps.forEach(step => {
    if (step.step_type !== 'condition') return;
    const target = Number(step.payload?.then_position);
    if (!Number.isInteger(target)) return;
    const targetId = beforeIds[target];
    const next = targetId === undefined ? -1 : afterIds.indexOf(targetId);
    step.payload = { ...(step.payload || {}), then_position: next };
  });
  return steps;
};

const matchesCondition = (step, scenario) => {
  const payload = step.payload || {};
  if (payload.attribute !== 'pipeline_stage') return false;
  const current = scenario.stageValue ?? '';
  const expected = String(payload.value ?? '');
  return payload.operator === 'not_equals'
    ? current !== expected
    : current === expected;
};

// Client-side dry run: walks the ordered steps without touching the backend.
// `scenario` lets the user preview branches (e.g. a funnel stage value).
// Backward jumps are followed a bounded number of times, then reported.
export const simulateFlow = (steps = [], scenario = {}) => {
  const entries = [];
  const warnings = [];
  const visited = new Map();
  const maxVisits = steps.length * 2 + 4;

  let position = 0;
  let guard = 0;

  while (position >= 0 && position < steps.length && guard < maxVisits) {
    guard += 1;
    const step = steps[position];
    const visits = (visited.get(position) || 0) + 1;
    visited.set(position, visits);

    if (visits > 3) {
      warnings.push({ position, code: 'loop' });
      break;
    }

    entries.push({
      position,
      type: step.step_type,
      detail: stepDetail(step),
      branch: null,
    });

    if (step.step_type === 'stop') break;

    if (step.step_type === 'condition') {
      const matched = matchesCondition(step, scenario);
      const target = matched
        ? Number(step.payload?.then_position)
        : position + 1;
      entries[entries.length - 1].branch = matched ? 'yes' : 'no';
      if (
        matched &&
        (!Number.isInteger(target) || target < 0 || target >= steps.length)
      ) {
        warnings.push({ position, code: 'bad_jump' });
        break;
      }
      position = target;
    } else {
      position += 1;
    }
  }

  if (guard >= maxVisits) {
    warnings.push({ position, code: 'loop' });
  }

  return { entries, warnings };
};

export const unreachablePositions = steps => {
  const reached = new Set([0]);
  const queue = [0];

  while (queue.length) {
    const position = queue.shift();
    const step = steps[position];

    if (step && step.step_type !== 'stop') {
      const next = position + 1;
      if (next < steps.length && !reached.has(next)) {
        reached.add(next);
        queue.push(next);
      }
    }

    if (step && step.step_type === 'condition') {
      const target = Number(step.payload?.then_position);
      if (
        Number.isInteger(target) &&
        target >= 0 &&
        target < steps.length &&
        !reached.has(target)
      ) {
        reached.add(target);
        queue.push(target);
      }
    }
  }

  return steps.map((_, index) => index).filter(index => !reached.has(index));
};
